import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import { isDemoMode, spoofText } from '@/utils/demoSpoof';

export const TOOLTIP_ID = 'app-tooltip';

const CLASSES = 'pointer-events-none fixed left-0 top-0 z-[10000] w-max max-w-72 rounded bg-gray-900 px-2.5 py-2 text-left text-xs font-normal leading-5 text-white shadow-lg';

let bubble = null;
let anchor = null;

function settings(value) {
  const { text, placement, spoof } = value !== null && typeof value === 'object' ? value : { text: value };
  return { text: String(text ?? '').trim(), placement: placement || 'bottom', spoof: spoof || false };
}

function displayText({ text, spoof }) {
  if (!spoof || !isDemoMode()) return text;
  return spoofText(text, spoof === true ? null : spoof);
}

function onKeydown(e) {
  if (e.key === 'Escape' && anchor) hide(anchor);
}

function onScroll() {
  if (anchor) hide(anchor);
}

function show(el) {
  const { text, placement } = el._tooltip;
  if (!text) return;
  if (anchor && anchor !== el) hide(anchor);
  if (!bubble) {
    bubble = document.createElement('div');
    bubble.id = TOOLTIP_ID;
    bubble.setAttribute('role', 'tooltip');
    bubble.className = CLASSES;
  }
  bubble.textContent = displayText(el._tooltip);
  bubble.style.visibility = 'hidden';
  document.body.appendChild(bubble);
  anchor = el;
  el._tooltipDescribedBy = el.getAttribute('aria-describedby');
  el.setAttribute('aria-describedby', [el._tooltipDescribedBy, TOOLTIP_ID].filter(Boolean).join(' '));
  document.addEventListener('keydown', onKeydown);
  window.addEventListener('scroll', onScroll, true);
  computePosition(el, bubble, {
    strategy: 'fixed',
    placement,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  }).then(({ x, y }) => {
    if (anchor !== el) return;
    Object.assign(bubble.style, { left: `${x}px`, top: `${y}px`, visibility: 'visible' });
  });
}

function hide(el) {
  if (anchor !== el) return;
  anchor = null;
  if (el._tooltipDescribedBy) el.setAttribute('aria-describedby', el._tooltipDescribedBy);
  else el.removeAttribute('aria-describedby');
  bubble.remove();
  document.removeEventListener('keydown', onKeydown);
  window.removeEventListener('scroll', onScroll, true);
}

export default {
  mounted(el, binding) {
    el._tooltip = settings(binding.value);
    el._tooltipShow = () => show(el);
    el._tooltipHide = () => hide(el);
    ['mouseenter', 'focus'].forEach((type) => el.addEventListener(type, el._tooltipShow));
    ['mouseleave', 'blur', 'click'].forEach((type) => el.addEventListener(type, el._tooltipHide));
  },
  updated(el, binding) {
    el._tooltip = settings(binding.value);
    if (anchor !== el) return;
    if (el._tooltip.text) bubble.textContent = displayText(el._tooltip);
    else hide(el);
  },
  beforeUnmount(el) {
    hide(el);
    ['mouseenter', 'focus'].forEach((type) => el.removeEventListener(type, el._tooltipShow));
    ['mouseleave', 'blur', 'click'].forEach((type) => el.removeEventListener(type, el._tooltipHide));
  },
};
